/*
 * Client-side port of the `clean_html.py` trial script: strips Word/rich-editor
 * paste junk and normalises table/typography/image styling on report page HTML,
 * so AI-generated, pasted, and hand-edited content ends up looking consistent.
 * Runs entirely in the browser against a DOM parsed from the editor's HTML string.
 */

const FONT = "Arial, Helvetica, sans-serif";

const TABLE_STYLE =
	"border-collapse: collapse; width: 100%; table-layout: fixed; " +
	`word-wrap: break-word; font-family: ${FONT};`;

const TOTAL_LABELS = new Set(["Total", "Total Water Reused", "% Water Reused", "Grand Total"]);

const NARROW_HEADERS = new Set([
	"sr. no.", "sr.no.", "s.no.", "sr no", "sr. no", "sno",
	"no.", "no", "#",
	"unit", "uom", "input uom",
	"year", "date",
]);

// Per-element style props that must never win over the cell-level style applied in step 5.
const CELL_INNER_STRIP = new Set(["font-size", "font-family", "color", "background", "background-color"]);

const STRIP_EXACT = new Set([
	"MsoNormal", "MsoNormalTable", "MsoListParagraph", "MsoCaption",
	"TextRun", "NormalTextRun", "EOP", "Paragraph",
	"TableCellContent", "OutlineElement",
	"Selected", "FieldRange", "TableRow", "Table", "Ltr",
	"FirstRow", "FirstCol", "LastRow", "LastCol",
	"SpellingErrorV2Themed", "HighContrastShading",
	"token-text", "threadScrollVars", "convSearchResultHighlight",
	"thread-content-margin",
]);

const STRIP_STARTS = [
	"SCXW", "BCX", "AdvancedProofing", "ContextualSpelling",
	"SpellingError", "SimilarityReviewed", "SimilarityUnreviewed",
	"AddInCritique",
	"pointer-events", "scroll-mb-", "scroll-mt-",
];

const REMOVE_ATTR_EXACT = new Set([
	"role", "valign", "border", "cellspacing", "cellpadding",
	"align", "width", "height", "dir", "nowrap",
]);
const REMOVE_ATTR_PREFIXES = ["data-", "aria-", "lang", "xml:lang"];

const PARA_DROP = new Set(["margin-left", "text-indent", "padding-left", "margin-right", "text-justify"]);

const IMG_STYLE = "display: block; margin: 16px auto; width: 100%; max-width: 640px; height: auto;";
const WRAP_STYLE = "text-align: center; margin: 16px 0;";

// Matches whitespace plus the non-breaking space Word/rich-editor pastes leave behind.
const NBSP_RE = /[\s ]+/g;

function isWordClass(cls) {
	return STRIP_EXACT.has(cls) || STRIP_STARTS.some((p) => cls.startsWith(p));
}

function parseStyle(styleStr) {
	const pairs = [];
	(styleStr || "").split(";").forEach((part) => {
		const trimmed = part.trim();
		const sepIdx = trimmed.indexOf(":");
		if (sepIdx === -1) return;
		const name = trimmed.slice(0, sepIdx).trim();
		const value = trimmed.slice(sepIdx + 1).trim();
		if (name) pairs.push([name, value]);
	});
	return pairs;
}

function renderStyle(pairs) {
	return pairs.map(([n, v]) => `${n}: ${v}`).join("; ");
}

function cleanStyle(styleStr, options) {
	const dropHeight = options && options.dropHeight;
	const dropWidth = options && options.dropWidth;
	const kept = parseStyle(styleStr).filter(([name]) => {
		const n = name.toLowerCase();
		if (n.startsWith("mso-")) return false;
		if (dropHeight && n === "height") return false;
		if (dropWidth && n === "width") return false;
		return true;
	});
	return renderStyle(kept);
}

function shouldDropAttr(name) {
	const n = name.toLowerCase();
	return REMOVE_ATTR_EXACT.has(n) || REMOVE_ATTR_PREFIXES.some((p) => n.startsWith(p));
}

function getText(el) {
	return (el.textContent || "").replace(NBSP_RE, " ").trim();
}

function looksNumeric(text) {
	const t = text.trim().replace(/,/g, "");
	if (!t) return false;
	return !Number.isNaN(Number(t));
}

function unwrapElement(el) {
	const parent = el.parentNode;
	if (!parent) return;
	while (el.firstChild) {
		parent.insertBefore(el.firstChild, el);
	}
	parent.removeChild(el);
}

function intAttr(el, name, fallback) {
	const raw = parseInt(el.getAttribute(name), 10);
	return Number.isNaN(raw) ? fallback : Math.max(1, raw);
}

// -- Bullet-list conversion --------------------------------------------------

function nextElementSkippingText(node) {
	let sib = node.nextSibling;
	while (sib && sib.nodeType === Node.TEXT_NODE) {
		sib = sib.nextSibling;
	}
	return sib;
}

function isBulletP(node) {
	return !!node && node.nodeType === Node.ELEMENT_NODE && node.tagName === "P" &&
		node.classList.contains("MsoListParagraph");
}

function convertBullets(root) {
	const doc = root.ownerDocument;
	const visited = new Set();

	Array.from(root.querySelectorAll("p.MsoListParagraph")).forEach((p) => {
		if (visited.has(p)) return;

		const group = [p];
		let sib = nextElementSkippingText(p);
		while (isBulletP(sib)) {
			group.push(sib);
			sib = nextElementSkippingText(sib);
		}

		const ul = doc.createElement("ul");
		group.forEach((bp) => {
			visited.add(bp);
			const li = doc.createElement("li");

			const bulletSpan = Array.from(bp.querySelectorAll("span")).find((span) => {
				const style = span.getAttribute("style") || "";
				return style.includes("mso-list") && style.includes("Ignore");
			});
			if (bulletSpan) bulletSpan.remove();

			while (bp.firstChild) {
				li.appendChild(bp.firstChild);
			}
			ul.appendChild(li);
		});

		group[0].replaceWith(ul);
		group.slice(1).forEach((bp) => bp.remove());
	});

	return root;
}

// -- Column alignment / width inference --------------------------------------

function analyzeColumns(rowCells) {
	if (!rowCells.length) return [];

	const maxCols = Math.max(...rowCells.map((r) => r.length));
	const headerRow = rowCells[0];
	const dataRows = rowCells.slice(1);

	const alignments = [];
	for (let colIdx = 0; colIdx < maxCols; colIdx++) {
		if (colIdx === 0) {
			alignments.push("left");
			continue;
		}

		const hdr = colIdx < headerRow.length ? getText(headerRow[colIdx]).toLowerCase() : "";

		if (hdr === "uom" || hdr === "unit") {
			alignments.push("center");
			continue;
		}

		const dataTexts = [];
		dataRows.forEach((row) => {
			if (colIdx < row.length) {
				const t = getText(row[colIdx]);
				if (t) dataTexts.push(t);
			}
		});

		if (!dataTexts.length) {
			alignments.push("center");
			continue;
		}

		const numeric = dataTexts.filter(looksNumeric).length;
		if (numeric / dataTexts.length >= 0.6) {
			alignments.push("right");
			continue;
		}

		const avgLen = dataTexts.reduce((s, t) => s + t.length, 0) / dataTexts.length;
		alignments.push(avgLen > 25 ? "left" : "center");
	}

	return alignments;
}

function logicalColData(rowCells) {
	const occupied = new Map(); // slot -> remaining rowspan count
	const colHdr = new Map();
	const colData = new Map();
	const colHasRs = new Map();
	const seen = new Set();

	rowCells.forEach((cells, rowIdx) => {
		const occupiedNow = new Set(occupied.keys());
		Array.from(occupied.entries()).forEach(([slot, remaining]) => {
			if (remaining > 1) occupied.set(slot, remaining - 1);
			else occupied.delete(slot);
		});

		let lc = 0;
		cells.forEach((cell) => {
			while (occupiedNow.has(lc)) lc += 1;

			const rs = intAttr(cell, "rowspan", 1);
			const cs = intAttr(cell, "colspan", 1);
			const text = getText(cell);

			for (let s = 0; s < cs; s++) {
				const slot = lc + s;
				if (rs > 1 && !occupied.has(slot)) occupied.set(slot, rs - 1);
				if (rowIdx === 0) {
					colHdr.set(slot, text);
				} else {
					if (!colData.has(slot)) colData.set(slot, []);
					if (!colHasRs.has(slot)) colHasRs.set(slot, false);
					if (rs > 1) colHasRs.set(slot, true);
				}
			}

			if (rowIdx !== 0 && !seen.has(cell) && text) {
				for (let s = 0; s < cs; s++) {
					const slot = lc + s;
					if (!colData.has(slot)) colData.set(slot, []);
					colData.get(slot).push(text);
				}
			}

			seen.add(cell);
			lc += cs;
		});
	});

	const maxHdrKey = colHdr.size ? Math.max(...colHdr.keys()) : -1;
	const maxDataKey = colData.size ? Math.max(...colData.keys()) : -1;
	const n = Math.max(maxHdrKey, maxDataKey) + 1;

	const colHdrArr = [];
	const colDataArr = [];
	const colHasRsArr = [];
	for (let c = 0; c < n; c++) {
		colHdrArr.push(colHdr.get(c) || "");
		colDataArr.push(colData.get(c) || []);
		colHasRsArr.push(colHasRs.get(c) || false);
	}
	return { colHdr: colHdrArr, colDataTexts: colDataArr, colHasRowspan: colHasRsArr };
}

function colWidths(rowCells, colAlignments) {
	const nCols = colAlignments.length;
	if (nCols === 0) return [];
	if (nCols === 1) return [100.0];

	const { colHdr, colDataTexts, colHasRowspan } = logicalColData(rowCells);

	const NARROW_W = 8.0;
	const MIN_FLEX_PCT = 8.0; // floor so no text/numeric column collapses when a sibling dominates
	const ROWSPAN_FLOOR = 10.0;
	const LONG_HDR_FLOOR = 12.0;
	const LONG_HDR_LIMIT = 15;

	const colAlen = [];
	for (let c = 0; c < nCols; c++) {
		const texts = colDataTexts[c] || [];
		colAlen.push(texts.length ? texts.reduce((s, t) => s + t.length, 0) / texts.length : 0.0);
	}

	const classes = [];
	for (let colIdx = 0; colIdx < nCols; colIdx++) {
		const hdr = (colHdr[colIdx] || "").toLowerCase();
		const alen = colAlen[colIdx];
		const align = colAlignments[colIdx];
		if (NARROW_HEADERS.has(hdr)) classes.push("narrow");
		else if (align === "right") classes.push("numeric");
		else if (alen > 0 && alen < 12 && hdr.length <= 12) classes.push("narrow");
		else classes.push("text");
	}

	// Text and numeric columns share the space left after narrow columns,
	// weighted by sqrt(avg content length) rather than the raw length itself.
	// A linear weight lets one long-sentence column (e.g. "Action / Impact")
	// starve every other column down to a sliver, and a numeric column with
	// long decimal/comma-formatted values (e.g. "2,533.4712") would otherwise
	// get squeezed just as hard as one holding short integers. Square-rooting
	// compresses that spread — a column ten times "longer" only pulls ~3x the
	// width of a shorter sibling, not 10x — which better matches how tables
	// actually need to look: short-value columns should never wrap, while a
	// paragraph column wrapping across multiple lines is normal and fine.
	const flexIdxs = classes.reduce((acc, c, i) => (c !== "narrow" ? [...acc, i] : acc), []);
	const nNarrow = classes.filter((c) => c === "narrow").length;

	const remaining = Math.max(0.0, 100.0 - nNarrow * NARROW_W);

	const flexWeights = flexIdxs.map((i) => Math.sqrt(Math.max(colAlen[i], 4.0)));
	const totalWeight = flexWeights.reduce((s, v) => s + v, 0);

	const widths = classes.map((cls, colIdx) => {
		if (cls === "narrow") return NARROW_W;
		const weight = flexWeights[flexIdxs.indexOf(colIdx)];
		return totalWeight > 0
			? (remaining * weight) / totalWeight
			: remaining / Math.max(flexIdxs.length, 1);
	});

	for (let colIdx = 0; colIdx < nCols; colIdx++) {
		let floor = 0.0;
		if (classes[colIdx] !== "narrow") floor = Math.max(floor, MIN_FLEX_PCT);
		if (colHasRowspan[colIdx]) floor = Math.max(floor, ROWSPAN_FLOOR);
		if (classes[colIdx] === "text") {
			const hdr = (colHdr[colIdx] || "").toLowerCase();
			if (hdr.length > LONG_HDR_LIMIT) floor = Math.max(floor, LONG_HDR_FLOOR);
		}
		if (floor) widths[colIdx] = Math.max(widths[colIdx], floor);
	}

	const total = widths.reduce((s, w) => s + w, 0);
	if (total <= 0) return new Array(nCols).fill(100.0 / nCols);
	const scale = 100.0 / total;
	return widths.map((w) => w * scale);
}

function cellStyle(align, nCols, options) {
	const isHeader = !!(options && options.isHeader);
	const isTotal = !!(options && options.isTotal);
	const colWidthPct = options && options.colWidthPct;

	let fontSize, pv, ph, pr;
	if (nCols >= 10) {
		fontSize = "10px"; pv = "4px"; ph = "5px"; pr = "8px";
	} else if (nCols >= 7) {
		fontSize = "11px"; pv = "5px"; ph = "6px"; pr = "9px";
	} else {
		fontSize = "13px"; pv = "8px"; ph = "10px"; pr = "14px";
	}

	const padding = align === "right" ? `${pv} ${pr} ${pv} ${ph}` : `${pv} ${ph}`;

	const props = [
		"border: 1px solid #b0b0b0",
		`padding: ${padding}`,
		`font-family: ${FONT}`,
		`font-size: ${fontSize}`,
		`text-align: ${align}`,
	];
	if (colWidthPct !== null && colWidthPct !== undefined) {
		props.push(`width: ${colWidthPct.toFixed(1)}%`);
	}
	if (isHeader) props.push("background: #f2f2f2", "font-weight: bold");
	else if (isTotal) props.push("background: #fafafa", "font-weight: bold");

	return props.join("; ") + ";";
}

// -- Empty-column removal -----------------------------------------------------

function dropEmptyColumns(table) {
	const rows = Array.from(table.querySelectorAll("tr"));
	if (!rows.length) return;

	const rowData = rows.map((row) => {
		const cells = Array.from(row.querySelectorAll("td, th"));
		const spans = cells.map((c) => intAttr(c, "colspan", 1));
		return { cells, spans };
	});

	const maxCols = rowData.reduce((m, { spans }) => Math.max(m, spans.reduce((s, v) => s + v, 0)), 0);
	if (maxCols === 0) return;

	const slotTexts = Array.from({ length: maxCols }, () => []);
	rowData.forEach(({ cells, spans }) => {
		let slot = 0;
		cells.forEach((cell, i) => {
			const colspan = spans[i];
			const text = getText(cell);
			for (let s = 0; s < colspan; s++) {
				if (slot + s < maxCols) slotTexts[slot + s].push(text);
			}
			slot += colspan;
		});
	});

	const emptySlots = new Set();
	for (let c = 0; c < maxCols; c++) {
		if (slotTexts[c].length && slotTexts[c].every((t) => !t)) emptySlots.add(c);
	}
	if (!emptySlots.size) return;

	rowData.forEach(({ cells, spans }) => {
		let slot = 0;
		cells.forEach((cell, i) => {
			const colspan = spans[i];
			let allEmpty = true;
			for (let s = 0; s < colspan; s++) {
				if (!emptySlots.has(slot + s)) allEmpty = false;
			}
			if (allEmpty) cell.remove();
			slot += colspan;
		});
	});
}

// -- Typography ---------------------------------------------------------------

function styleMapFrom(el) {
	const props = new Map();
	parseStyle(el.getAttribute("style") || "").forEach(([n, v]) => props.set(n.toLowerCase(), v));
	return props;
}

function applyTypography(root) {
	Array.from(root.querySelectorAll("p")).forEach((p) => {
		const inCell = !!p.closest("td, th");
		const props = styleMapFrom(p);
		PARA_DROP.forEach((k) => props.delete(k));
		props.set("font-family", FONT);
		props.set("line-height", "1.5");
		if (inCell) {
			props.set("margin", "0");
		} else {
			props.set("font-size", "14px");
			props.set("text-align", "left");
			props.set("margin", "0 0 10px 0");
		}
		p.setAttribute("style", renderStyle(Array.from(props.entries())));
	});

	[["h1", "32px"], ["h2", "24px"], ["h3", "18px"], ["h4", "16px"], ["h5", "14px"], ["h6", "13px"]]
		.forEach(([tagName, size]) => {
			Array.from(root.querySelectorAll(tagName)).forEach((h) => {
				const props = styleMapFrom(h);
				props.set("font-family", FONT);
				props.set("font-size", size);
				props.set("font-weight", "bold");
				props.set("color", "#222");
				props.set("line-height", "1.3");
				props.set("margin", "0 0 10px 0");
				h.setAttribute("style", renderStyle(Array.from(props.entries())));
			});
		});

	Array.from(root.querySelectorAll("ul, ol")).forEach((ul) => {
		const props = styleMapFrom(ul);
		props.set("margin", "0 0 10px 0");
		props.set("padding-left", "24px");
		ul.setAttribute("style", renderStyle(Array.from(props.entries())));
	});

	Array.from(root.querySelectorAll("li")).forEach((li) => {
		const props = styleMapFrom(li);
		props.set("font-family", FONT);
		props.set("font-size", "14px");
		props.set("line-height", "1.5");
		li.setAttribute("style", renderStyle(Array.from(props.entries())));
	});
}

// -- Images --------------------------------------------------------------------

function standardizeImages(root) {
	const doc = root.ownerDocument;
	Array.from(root.querySelectorAll("img")).forEach((img) => {
		img.setAttribute("style", IMG_STYLE);
		img.removeAttribute("width");
		img.removeAttribute("height");

		const parent = img.parentElement;
		const alreadyWrapped = !!parent && parent.tagName === "DIV" &&
			(parent.getAttribute("style") || "").includes("center");

		if (!alreadyWrapped) {
			const wrapper = doc.createElement("div");
			wrapper.setAttribute("style", WRAP_STYLE);
			img.replaceWith(wrapper);
			wrapper.appendChild(img);
		}
	});
}

// -- Main pipeline --------------------------------------------------------------

function clean(root) {
	const doc = root.ownerDocument;

	// Step 0: MsoListParagraph runs -> <ul><li> (must run before class-stripping)
	convertBullets(root);

	// Step 1: remove HTML comments
	const walker = doc.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
	const comments = [];
	let node = walker.nextNode();
	while (node) {
		comments.push(node);
		node = walker.nextNode();
	}
	comments.forEach((c) => c.remove());

	// Step 2: clean styles, classes, and junk attributes on every element
	Array.from(root.querySelectorAll("*")).forEach((tag) => {
		const tagName = tag.tagName.toLowerCase();

		const raw = tag.getAttribute("style");
		if (raw) {
			const cleaned = cleanStyle(raw, {
				dropHeight: tagName === "tr" || tagName === "td" || tagName === "th",
				dropWidth: tagName === "td" || tagName === "th",
			});
			if (cleaned) tag.setAttribute("style", cleaned);
			else tag.removeAttribute("style");
		}

		const classes = Array.from(tag.classList);
		if (classes.length) {
			const kept = classes.filter((c) => !isWordClass(c));
			if (kept.length) tag.setAttribute("class", kept.join(" "));
			else tag.removeAttribute("class");
		}

		Array.from(tag.attributes).forEach((attr) => {
			if (!shouldDropAttr(attr.name)) return;
			if (tagName === "img" && ["width", "height"].includes(attr.name.toLowerCase())) return;
			tag.removeAttribute(attr.name);
		});
	});

	// Step 3: unwrap <div> wrappers inside <td>/<th>
	Array.from(root.querySelectorAll("td div, th div")).forEach(unwrapElement);

	// Step 4: unwrap bare <span> tags (repeat until stable)
	let changed = true;
	while (changed) {
		changed = false;
		const spans = Array.from(root.querySelectorAll("span"));
		for (let i = 0; i < spans.length; i++) {
			if (!spans[i].attributes.length) {
				unwrapElement(spans[i]);
				changed = true;
			}
		}
	}

	// Step 4b: unwrap attribute-less <div>/<section> wrappers
	changed = true;
	while (changed) {
		changed = false;
		const wrappers = Array.from(root.querySelectorAll("div, section"));
		for (let i = 0; i < wrappers.length; i++) {
			if (!wrappers[i].attributes.length) {
				unwrapElement(wrappers[i]);
				changed = true;
			}
		}
	}

	// Step 4c: strip per-element typography overrides inside table cells
	Array.from(root.querySelectorAll("td, th")).forEach((cell) => {
		Array.from(cell.querySelectorAll("*")).forEach((inner) => {
			const raw = inner.getAttribute("style");
			if (!raw) return;
			const kept = parseStyle(raw).filter(([n]) => !CELL_INNER_STRIP.has(n.toLowerCase()));
			if (kept.length) inner.setAttribute("style", renderStyle(kept));
			else inner.removeAttribute("style");
		});
	});

	// Remove deprecated <font> tags, preserving their text content
	Array.from(root.querySelectorAll("font")).forEach(unwrapElement);

	// Step 4d: remove leftover <colgroup> elements (fixed pixel widths fight table-layout: fixed)
	Array.from(root.querySelectorAll("colgroup")).forEach((cg) => cg.remove());

	// Step 5: uniform table styles
	Array.from(root.querySelectorAll("table")).forEach((table) => {
		table.setAttribute("style", TABLE_STYLE);
		["border", "cellspacing", "cellpadding", "width", "height"].forEach((a) => table.removeAttribute(a));

		dropEmptyColumns(table);

		const rows = Array.from(table.querySelectorAll("tr"));
		const nRows = rows.length;

		const rowCells = rows.map((r) => Array.from(r.querySelectorAll("td, th")));
		const rowCellsNonempty = rowCells.filter((rc) => rc.length);
		const colAlignments = analyzeColumns(rowCellsNonempty);
		const nCols = colAlignments.length;
		const widths = colWidths(rowCellsNonempty, colAlignments);

		rows.forEach((row, rowIdx) => {
			const cells = rowCells[rowIdx];
			if (!cells.length) return;

			const isHeader = rowIdx === 0;
			const firstText = getText(cells[0]);
			const isTotal = TOTAL_LABELS.has(firstText) && rowIdx >= nRows - 2;

			cells.forEach((cell, colIdx) => {
				const align = colIdx < nCols ? colAlignments[colIdx] : "center";
				const wPct = isHeader && colIdx < widths.length ? widths[colIdx] : null;
				cell.setAttribute("style", cellStyle(align, nCols, { isHeader, isTotal, colWidthPct: wPct }));
			});
		});
	});

	// Step 6: export-safe typography
	applyTypography(root);

	// Step 7: standardise images
	standardizeImages(root);

	return root;
}

/**
 * Beautify a report page's HTML: strips Word/rich-editor paste junk and applies
 * consistent table, typography, and image styling. Returns a new HTML string;
 * does not mutate the input.
 */
export function beautifyReportHtml(htmlString) {
	if (!htmlString || !htmlString.trim()) return htmlString;

	const parser = new DOMParser();
	const doc = parser.parseFromString(htmlString, "text/html");
	clean(doc.body);
	return doc.body.innerHTML;
}

export default beautifyReportHtml;
